import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { LogOut } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { SidebarTrigger } from '../ui/sidebar';
import { cn } from '@/lib/utils';
import { routes } from '@/routing';
import { useTenantStaffAuth } from '@/hooks/use-tenant-staff-auth';
import { useTranslation } from 'react-i18next';

const formatTenantStaffRole = (role?: string) => {
  if (role === 'tenant_owner') return 'Owner';
  if (role === 'tenant_staff_admin') return 'Admin';
  if (role === 'tenant_staff_member') return 'Member';
  return role || 'Staff';
};

export const TenantStaffNavHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { staff, logout } = useTenantStaffAuth();
  const { t } = useTranslation();

  const getActiveTitle = () => {
    switch (location.pathname) {
      case routes.tenantStaffCampaigns:
        return {
          title: t('tenantStaff.navHeader.campaigns.title'),
          description: t('tenantStaff.navHeader.campaigns.description'),
        };
      case routes.tenantStaffAdvertisers:
        return {
          title: t('tenantStaff.navHeader.advertisers.title'),
          description: t('tenantStaff.navHeader.advertisers.description'),
        };
      case routes.tenantStaffUsers:
        return {
          title: t('tenantStaff.navHeader.users.title'),
          description: t('tenantStaff.navHeader.users.description'),
        };
      case routes.tenantStaffConsolidatedReport:
        return {
          title: t('tenantStaff.navHeader.reports.title'),
          description: t('tenantStaff.navHeader.reports.description'),
        };
      default:
        return {
          title: t('tenantStaff.navHeader.dashboard.title'),
          description: t('tenantStaff.navHeader.dashboard.description'),
        };
    }
  };

  const activeTitle = getActiveTitle();

  const handleLogout = () => {
    logout();
    navigate(routes.tenantStaffLogin, { replace: true });
  };

  return (
    <header
      className={cn(
        'flex shrink-0 items-center gap-2 border-b border-admin-border-light bg-admin-bg-card',
        'sticky top-0 z-40 transition-all duration-200 ease-in-out shadow-sm'
      )}
    >
      <div className="flex items-center justify-between w-full gap-2 px-4 py-3">
        <Link
          to={routes.tenantStaffDashboard}
          className="flex items-center gap-3 transition-opacity hover:opacity-80"
        >
          <div className="grid flex-1 text-sm leading-tight text-left">
            <span className="text-lg font-extrabold truncate text-admin-text-dark">
              {activeTitle.title}
            </span>
            <span className="text-sm truncate text-admin-text-muted">
              {activeTitle.description}
            </span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          {staff && (
            <div className="items-center hidden gap-2 md:flex">
              <span className="text-sm text-admin-text-muted">
                {staff.email}
              </span>
              <Badge className="text-xs bg-slate-200 text-slate-700 hover:bg-slate-200">
                {formatTenantStaffRole(staff.role)}
              </Badge>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-admin-text-muted hover:text-admin-text-dark"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('navigation.logout')}
          </Button>
          <SidebarTrigger
            className="w-8 h-8 transition-colors rounded-lg hover:bg-admin-bg-muted"
            aria-label={t('navHeader.sidebarToggleAriaLabel')}
          />
        </div>
      </div>
    </header>
  );
};
